/**
 * @render react
 * @name AuthorBio
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Image } from 'Components/atoms/Image';
import { Heading } from 'Components/molecules/Heading';
import { Social } from 'Components/molecules/Social';

import './_style.scss';

/**
 * AuthorBio component
 * @param {string} bio - text for the author bio
 * @param {string} company - company name where author is positioned
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {string} headingSize - size of the heading
 * @param {string} isHeading - whether to use heading tags for the author name
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {string} jobTitle - job role of the author
 * @param {string} name - name of the author
 * @param {Object} photo - object containing author photo src and alt text
 * @param {array} socialPlatforms - array to be passed to Social component
 */
const AuthorBio = ({
  bio,
  company,
  context: { cssPrefix },
  extraClassNames,
  headingSize,
  isHeading,
  loadStyles,
  jobTitle,
  name,
  photo,
  socialPlatforms,
}) => (
  <div
    className={classNames({
      [`${cssPrefix}__author-bio`]: loadStyles,
      ...extraClassNames,
    })}
  >
    {photo && photo.src && (
      <div className={`${cssPrefix}__author-bio--photo`}>
        <Image
          size="author"
          {...photo}
        />
      </div>
    )}
    <div className={`${cssPrefix}__author-bio--author-details`}>
      {name && isHeading ? (
        <Heading
          size={headingSize}
          extraClassNames={{
            [`${cssPrefix}__author-bio--name`]: true,
          }}
        >
          <span>{name}</span>
        </Heading>
      ) : (
        <div className={`${cssPrefix}__author-bio--name`}>
          {name}
        </div>
      )}

      {(jobTitle || company) && (
        <div className={`${cssPrefix}__author-bio--role`}>
          {jobTitle}
          {(jobTitle && company) && (', ')}
          {company && (
            <em>{company}</em>
          )}
        </div>
      )}

      {bio && (
        <div className={`${cssPrefix}__author-bio--bio`}>
          {bio}
        </div>
      )}

      {socialPlatforms && (
        <Social platforms={socialPlatforms} />
      )}
    </div>
  </div>
);

/**
 * Valid props
 */
AuthorBio.propTypes = {
  bio: PropTypes.oneOfType([
    PropTypes.shape({
      html: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  company: PropTypes.string,
  context: PropTypes.shape(),
  extraClassNames: PropTypes.shape(),
  headingSize: PropTypes.number,
  isHeading: PropTypes.bool,
  loadStyles: PropTypes.bool,
  jobTitle: PropTypes.string,
  name: PropTypes.string,
  photo: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  socialPlatforms: PropTypes.arrayOf(PropTypes.shape()),
};

/**
 * Default props
 */
AuthorBio.defaultProps = {
  bio: '',
  company: '',
  context: {},
  extraClassNames: {},
  headingSize: 1,
  isHeading: false,
  loadStyles: true,
  jobTitle: '',
  name: '',
  photo: {
    src: '',
    alt: '',
    height: 0,
    width: 0,
  },
  socialPlatforms: [],
};

export default withPolaris(AuthorBio);
